import React, { useEffect } from 'react';
import { useSelector, useBridgeApi, useLogger, useCurrency, useDispatch } from '~Hooks';
import AdyenTerminalComms, { PrintRow } from '@baemingo/adyen-terminal-comms';
import retry from 'async-retry';

import { useStorage } from '~/Components/Storage';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { CardDetails, Meta } from '~reducers';
import { AdyenRequest, AdyenRequestVariables } from './__queries__';
import { default as payAndVerify } from './pay';
import { AdyenRefundRequest, AdyenRefundRequestVariables } from './__queries__/AdyenRefundRequest';
import { createContext, useState } from 'react';
import { CURRENCY } from '../../../../__queries__';
import { SocketAPI } from '~Components/SocketManager';

const adyenKey = process.env.ADYEN_KEY!;
const adyenPass = process.env.ADYEN_PASS!;

export const securityKey = {
  adyenCryptoVersion: 1,
  keyIdentifier: adyenKey,
  keyVersion: 0,
  passphrase: adyenPass,
};

const ADYEN_REQEST = gql`
  mutation AdyenRequest($venueId: String!, $request: Json!) {
    adyenRequest(venueId: $venueId, request: $request, returnFullResponse: true) {
      success
      error
      response
    }
  }
`;

const ADYEN_BACKEND_REFUND_REQUEST = gql`
  mutation AdyenRefundRequest($transactionId: String, $amount: Int) {
    posAdyenRefundRequest(transactionId: $transactionId, amount: $amount) {
      success
      message
      method
      cardIdentifier
    }
  }
`;

/**
 * Dont use backend refund for these venues
 */
export const VENUE_IDS_WITH_CUSTOM_ADYEN = [
  // 'ckr4nt8c5y9w50824i6e6jjgb', // Chioi
  // 'cks8u9pucqqhn0823jkqf9rpi', // Villa Godthem
  // 'clggdf1ksz2gz0a20j4jlh9st', // Sorrelina
  // 'cllnmobt0sb5f0c86xokebvc0', // Sorr Orangeriet
  // 'cko40y9vlh8yi08730eb3uohb', // Trattorian
  // 'ckrkg6lvs0kzk0815nevy5ffu', // East
  // 'ckl0l1kbj2n2m0990t1e5d9sg', // Il tempo
  // 'cknpxo8d0aef708485l8mwoon', // Ricordi
  // 'cks8ou02zoc6y0c46v8o711ti', // Pas d art
  // 'ckq7rprvskm3w0b78idfx4w4g', // Prinsen
  /* 'cld2w1qmbmrhp0b875vnj8ol9', // Vapiano Sturegatan */
  'ck803ge4n05mq0171s2mv7b5y', // Gastrotek Zink
  'ckrkb9m87ytad0824n504xaut', // Gastrotek Zink
  'ckhw2lym11amr0764g8zcciuw', // Happie Hands
  /* 'cld2w1qmbmrhp0b875vnj8ol9', // Vapiano Sturegatan */
  /*   'cld2vytej8jr0083658vpbu3x', // Vapiano Vasagatan
  'cld2vud6krnx10b40yy8ppa4w', // Vapiano Gamla Stan
  'cld2wgm86r6aq0c33e6xmjiil', // Vapiano MoS
  'cld2w5jzf8oir0836c6goqf3x', // Vapiano GBG
  'cld2vpl9qs4390b78mcsrh50l', // Vapiano Emporia */
  'clx04hed8pphs0c69r7djlgpi', // MOMO Buffet
];

const AdyenContext = createContext<{
  addTransactionCheck: (serviceId: string) => void;
}>({
  addTransactionCheck: () => {},
});

export const useAdyen = () => {
  const [, , venueId] = location.hash.split('/');
  const [storageData] = useStorage();
  const adyenTerminals = storageData.adyenTerminals;
  const bridgeApi = useBridgeApi();
  const country = useSelector((state) => state.country);
  const logger = useLogger('adyen');
  const dispatch = useDispatch();
  const [adyenRequest] = useMutation<AdyenRequest, AdyenRequestVariables>(ADYEN_REQEST);
  const [adyenRefundRequest] = useMutation<AdyenRefundRequest, AdyenRefundRequestVariables>(
    ADYEN_BACKEND_REFUND_REQUEST,
  );

  const currency = useCurrency();

  useEffect(() => {
    if (!currency.code) {
      logger.error('Currency code is not set');
    }

    if (currency.code) {
      logger.info('Currency code is set: ' + currency.code);
    }

    if (currency.code === CURRENCY.SEK && Boolean(country) && country !== 'SE') {
      logger.error(`Currency code is SEK but country is set to ${country}`);
    }
  }, [currency.code]);

  const createTerminalInstance = (terminalId?: string) => {
    const instance = new AdyenTerminalComms(
      'device',
      {
        local: bridgeApi.adyenRequest,
        cloud: async (request: object) => {
          try {
            const res = await adyenRequest({
              variables: {
                venueId,
                request,
              },
            });
            return res.data?.adyenRequest?.response;
          } catch (e) {
            logger.warn((e as Error).message);
          }
          return null;
        },
      },
      currency.code, // This is the currency code used for this instance
    );

    // If we've specified a terminal ID, init the instance with its info
    if (terminalId) {
      const terminal = adyenTerminals.find((t) => t.POIID === terminalId);

      // There is no guarantee that we have added the terminal we are making the reqeust to,
      // but if adyen cloud is activated it will work anyway
      instance.init(securityKey, {
        ip: terminal ? terminal.IP : null,
        poiid: terminalId,
      });
    }

    return instance;
  };

  const pay = async (
    terminalId: string,
    input: {
      amount: number;
      transactionId: string;
      split?: boolean;
      paidAmount?: number;
      saleId: string;
      serviceId: string;
      socket?: SocketAPI;
      useSocket?: boolean;
    },
    onMetaChange?: (meta: Meta) => void,
  ): Promise<{
    success: boolean;
    message?: string;
    tip?: number;
    transactionId?: string;
    saleId?: string;
    serviceId: string;
    cardDetails?: CardDetails | null;
  }> => {
    const terminalInstance = createTerminalInstance(terminalId);

    return new Promise((resolve, reject) => {
      payAndVerify({
        terminalInstance,
        enableTipping: storageData.enableTipping,
        reference: input.transactionId,
        transactionId: input.transactionId,
        amount: input.amount,
        logger,
        customServiceId: input.serviceId,
        onComplete: ({ tip, transactionId, cardDetails }) => {
          resolve({
            success: true,
            tip,
            transactionId,
            cardDetails,
            serviceId: input.serviceId,
          });
        },
        onFail: (reason: string) => {
          logger.warn(`Payment failed, reason: ${reason}`, {
            id: input.transactionId,
          });
          reject(reason);
        },
        socket: input.socket,
        useSocket: input.useSocket,
        dispatch,
      });
    });
  };

  const transactionStatus = async (terminalId: string, saleId: string, serviceId: string) => {
    const terminalInstance = createTerminalInstance(terminalId);

    const res = await terminalInstance.transactionStatus(saleId, serviceId);

    return res;
  };

  const printImage = async (terminalId: string, imageBase64: string) => {
    const terminalInstance = createTerminalInstance(terminalId);

    return terminalInstance.printImage(imageBase64);
  };

  const print = async (terminalId: string, rows: PrintRow[]) => {
    const terminalInstance = createTerminalInstance(terminalId);

    // Work around for not being able to print receipts on S1F2 terminal / new adyen terminal software version
    // TODO: "TX_MESSAGE_IN_PROGRESS", needs a terminal state handler to know its ready before sending print
    // Retries 3 times, with an exponential factor of 2 (first it waits 1,5 secs then 3 etc)

    const res = await retry(
      async (_, attempt) => {
        logger.info(`Adyen print, attempt no ${attempt}`, {
          rows,
        });
        // if anything throws, we retry
        const printRes = await terminalInstance.print(rows);
        return printRes;
      },
      {
        retries: 5,
        minTimeout: 1500,
        randomize: false,
      },
    );

    if (res?.PrintResponse?.Response?.Result === 'Success') {
      logger.info('Adyen print result', {
        response: res,
        rows,
      });
      return { success: true };
    }

    logger.error('Adyen print error after 3 retries', {
      response: res,
      rows,
    });

    return { success: false };
  };

  const printQR = async (terminalId: string, code: string) => {
    const terminalInstance = createTerminalInstance(terminalId);

    const res = await retry(
      async (_, attempt) => {
        logger.info(`Adyen QR print, attempt no ${attempt}`, {
          code,
        });
        // if anything throws, we retry
        const printRes = await terminalInstance.printQR(code);
        return printRes;
      },
      {
        retries: 5,
        minTimeout: 1500,
        randomize: false,
      },
    );

    if (res?.PrintResponse?.Response?.Result === 'Success') {
      logger.info('Adyen QR print result', {
        response: res,
        code,
      });
      return { success: true };
    }

    logger.error('Adyen QR print error after 3 retries', {
      response: res,
      code,
    });

    return { success: false };
  };

  const refundPayment = async ({
    transactionId,
    createdAt,
    amount,
    terminalId,
  }: {
    transactionId: string;
    createdAt?: string;
    amount?: number;
    terminalId?: string;
  }): Promise<{ success: boolean; message?: string; transactionId?: string }> => {
    logger.info('Refund - Adyen Tip Refund initated for: ' + transactionId, {
      transactionId,
    });

    if (!VENUE_IDS_WITH_CUSTOM_ADYEN.includes(venueId)) {
      return new Promise<{ success: boolean; message?: string }>((resolve, reject) => {
        adyenRefundRequest({
          variables: {
            transactionId: transactionId,
            amount: amount,
          },
          fetchPolicy: 'no-cache',
        })
          .then(({ data }) => {
            if (!data?.posAdyenRefundRequest.success) {
              logger.error(
                'Refund - Adyen Backend Tip Refund failed on transactionId: ' + transactionId,
                {
                  transactionId,
                  errorMessage: data?.posAdyenRefundRequest.message,
                },
              );
              resolve({
                success: data?.posAdyenRefundRequest.success ?? false,
                message: data?.posAdyenRefundRequest.message,
              });

              return;
            }

            // Refund successful.
            logger.info(
              'Refund - Adyen Backend Tip Refund successful on transactionId: ' + transactionId,
              { transactionId },
            );

            resolve({
              success: data.posAdyenRefundRequest.success,
            });
          })
          .catch((e) => {
            logger.error(
              'Refund - Adyen Backend Tip Refund failed on transactionId: ' + transactionId,
              {
                transactionId,
                errorMessage: e.message,
              },
            );
            reject({
              success: false,
              message: e.message,
            });
          });
      });
    }

    if (terminalId) {
      logger.info('Refund - Adyen Tip Refund initated on terminal ' + terminalId, {
        terminalId,
        transactionId,
        amount,
      });
      const terminalInstance = createTerminalInstance(terminalId);

      const res = await terminalInstance.refund(transactionId, createdAt, amount);
      logger.info('Refund - Adyen Tip Refund terminal response', {
        response: res,
      });
      return res;
    } else {
      return new Promise<{ success: boolean; message?: string }>((reject) => {
        reject({
          success: false,
          message: 'No terminal found',
        });
      });
    }
  };

  const refundUnreferenced = async (
    terminalId: string,
    transactionId: string,
    amount: number,
  ): Promise<{ success: boolean; message?: string; transactionId?: string }> => {
    const terminalInstance = createTerminalInstance(terminalId);

    const res = await terminalInstance.refundUnreferenced(transactionId, amount);

    return res;
  };

  const abortPayment = async ({
    serviceId,
    terminalId,
  }: {
    serviceId: string;
    terminalId: string;
  }): Promise<{ success: boolean }> => {
    const terminalInstance = createTerminalInstance(terminalId);

    const res = await terminalInstance.abort('device', serviceId);

    return res;
  };

  const prompt = (id: string, title: string, options: any) => {
    const terminalInstance = createTerminalInstance(id);

    return terminalInstance.prompt(title, options);
  };

  const displayQR = async (terminalId: string, qr: string) => {
    const terminalInstance = createTerminalInstance(terminalId);

    return terminalInstance.displayQR({
      code: qr,
    });
  };

  const scanQRCode = async (terminalId: string) => {
    const terminalInstance = createTerminalInstance(terminalId);

    const id = Math.round(Math.random() * 1000000);
    return terminalInstance.startScan(id, 5000);
  };

  return {
    refundUnreferenced,
    prompt,
    createTerminalInstance,
    print,
    printQR,
    printImage,
    abortPayment,
    pay,
    transactionStatus,
    refundPayment,
    displayQR,
    scanQRCode,
  };
};

const TransactionCheck = (data: { terminalId: string; saleId: string; serviceId: string }) => {
  const adyen = useAdyen();

  useEffect(() => {
    const interval = setInterval(() => {
      adyen.transactionStatus(data.terminalId, data.saleId, data.serviceId).then((res) => {
        if (res.success) {
          clearInterval(interval);
        }
      });
    }, 1500);
  }, []);

  return null;
};
